import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { isNumber } from 'lodash';
import {
  BehaviorSubject,
  interval,
  Observable,
  Subscription,
  timer,
} from 'rxjs';
import {
  auditTime,
  filter,
  map,
  switchMap,
  take,
  tap,
  throttleTime,
} from 'rxjs/operators';
import {
  AxisCartesianLinear,
  BarDataset,
  Chart,
  ChartBarData,
  ChartData,
  ChartDoughnutData,
  ChartOptions,
  ChartsData,
  DoughnutDataset,
  LineDataset,
  ngxData,
  ngxSerie,
  Options,
  PieDataset,
  PolarAreaDataset,
  RadarDataset,
} from 'src/app/dashboard/models/chart';
import { UnitsTransmissionStatus } from 'src/app/units/models/unit-transmission-status';
import { UnitTransmissionStatusService } from 'src/app/units/services/unit-transmission-status.service';

@Component({
  selector: 'app-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
})
export class ChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() id?: number;
  @Input() type?: string;
  @Input() data?: ChartData | (ngxData | ngxSerie)[]; //
  @Input() options?: ChartOptions;
  @Input() library: string = 'chart.js';
  @Input() inLive?: boolean; //
  @Input() showExample?: boolean = false; //
  @Input() isEmpty?: boolean = false;
  @Input() colors?: string[] = [];

  @Input() styleClass: string = '';
  @Input() style: any;

  isEmptyData: boolean = true;
  totalUnits: number = 100;

  unitTxStates$: Observable<UnitsTransmissionStatus[]>;
  _unitTxStates$: Subscription | undefined;

  constructor(private unitTrasmitionService: UnitTransmissionStatusService) {
    this.unitTxStates$ = this.unitTrasmitionService.getUnitsTransmissionStates;
  }

  ngxCharts$: BehaviorSubject<(ngxData | ngxSerie)[]> = new BehaviorSubject<
    (ngxData | ngxSerie)[]
  >([]);
  pCharts$: BehaviorSubject<ChartsData> = new BehaviorSubject<ChartsData>({
    id: 0,
    labels: [],
    datasets: [],
  });

  colorScheme = {
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA'],
  };

  ngOnInit(): void {
    this.initChart();
  }
  ngOnDestroy(): void {
    this._unitTxStates$?.unsubscribe();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.library) {
      this.initChart();
      this.library;
    }
    if (changes.data) {
      this.initChart();
      this.data;
    }
  }

  private initChart(): void {
    if (
      (!this.showExample &&
        !this.isEmpty &&
        ((this.inLive && isNumber(this.id)) ||
          (this.inLive && this.type == 'advancedPieChart') ||
          this.data)) ||
      this.showExample
    )
      this.isEmptyData = false;
    if (!this.showExample) {
      if (
        (this.inLive && isNumber(this.id)) ||
        (this.inLive && this.type == 'advancedPieChart')
      ) {
        const bookshop: Record<string, (T: UnitsTransmissionStatus[]) => void> =
          {
            'NGX-Charts': (T) => this.updateUnitTxStatusForNgxChart(T),
            'chart.js': (T) => this.updateUnitTxStatusForChartJs(T),
          };
        // console.log('hola', bookshop.hasOwnProperty(this.library));
        this._unitTxStates$?.unsubscribe();
        this._unitTxStates$ = this.unitTxStates$
          .pipe(map((txs) => bookshop[this.library](txs)))
          .subscribe();
        if (this.library == 'chart.js') {
          this.type = 'doughnut';
          this.options = {
            animation: {
              duration: 0,
            },
            legend: {
              display: false,
            },
          };
        }
      } else {
        if (this.data) {
          if (this.colors) this.colorScheme = { domain: this.colors };
          // Por terminar cuando traigan data
          const bookshop: Record<string, () => void> = {
            'NGX-Charts': () => {
              this.ngxCharts$.next(this.data as (ngxData | ngxSerie)[]);
            },
            'chart.js': () => {
              this.pCharts$.next(this.data as ChartsData);
            },
          };
          interval(500)
            .pipe(
              take(2),
              tap(() => {
                bookshop[this.library]();
              })
            )
            .subscribe();
        }
      }
    } else {
      const bookshop: Record<string, (T?: string) => void> = {
        'chart.js': (T) => this.pCharts$.next(this.getChartJsExample(T)),
        'NGX-Charts': (T) => this.ngxCharts$.next(this.getNgxChartExample(T)),
      };
      interval(500)
        .pipe(
          take(2),
          tap(() => {
            bookshop[this.library](this.type);
          })
        )
        .subscribe();
    }
  }
  // ---------------------------------------------------------------------------
  private getChartJsExample(_type: string = 'bar'): ChartsData {
    const _charts: Record<string, ChartsData> = {
      bar: {
        id: 0,
        labels: ['Tx', 'All'],
        datasets: [
          {
            type: 'bar',
            label: 'Example Tx',
            backgroundColor: '#808080',
            data: [10, 100],
          },
        ],
      },
      horizontalBar: {
        id: 0,
        labels: ['Tx', 'All'],
        datasets: [
          {
            type: 'horizontalBar',
            label: 'Example Tx',
            backgroundColor: '#808080',
            data: [10, 100],
          },
        ],
      },
      doughnut: {
        id: 0,
        labels: ['Tx', 'All'],
        datasets: [
          {
            type: 'doughnut',
            data: [10, 100],
            backgroundColor: ['#800080', '#808080'],
            hoverBackgroundColor: ['#900090', '#909090'],
          },
        ],
      },
      line: {
        id: 0,
        labels: ['Tx', 'All'],
        datasets: [
          {
            type: 'line',
            label: 'Example Tx',
            data: [10, 100],
            fill: false,
            backgroundColor: '#808080',
            borderColor: '#404040',
          },
        ],
      },
      polarArea: {
        id: 0,
        labels: ['Tx', 'All', 'None'],
        datasets: [
          {
            type: 'polarArea',
            label: 'Example Tx',
            data: [50, 100, 70],
            backgroundColor: ['#800080', '#808080', '#008080'],
          },
        ],
      },
      pie: {
        id: 0,
        labels: ['Tx', 'All'],
        datasets: [
          {
            type: 'pie',
            data: [10, 100],
            backgroundColor: ['#800080', '#808080'],
            hoverBackgroundColor: ['#900090', '#909090'],
          },
        ],
      },
      radar: {
        id: 0,
        labels: ['Tx', 'All', 'None'],
        datasets: [
          {
            type: 'radar',
            label: 'Example Tx',
            data: [10, 100, 75],
            backgroundColor: '#808080',
            borderColor: '#C0C0C0',
            pointBackgroundColor: 'rgba(0,0,0,1)',
            pointBorderColor: '#808080',
            pointHoverBackgroundColor: '#808080',
            pointHoverBorderColor: 'rgba(0,0,0,1)',
          },
        ],
      },
      combo: {
        id: 0,
        labels: ['Tx', 'All'],
        datasets: [
          {
            type: 'bar',
            label: 'Example Tx',
            backgroundColor: '#808080',
            data: [10, 100],
          },
          {
            type: 'line',
            label: 'Example Tx',
            data: [10, 100],
            fill: false,
            backgroundColor: '#808080',
            borderColor: '#404040',
          },
        ],
      },
    };
    this.type = _charts.hasOwnProperty(_type) ? _type : 'bar';
    _type = this.type;
    this.options = {
      animation: {
        duration: 0,
      },
    };
    return _charts[_type ?? 'bar'];
  }
  private getNgxChartExample(
    _type: string = 'pieGrid'
  ): (ngxData | ngxSerie)[] {
    const _charts: Record<string, (ngxData | ngxSerie)[]> = {
      pieGrid: [
        {
          name: 'Example Tx',
          value: 10,
        },
      ],
      advancedPieChart: [
        {
          name: 'Example Tx1',
          value: 10,
        },
        {
          name: 'Example Tx2',
          value: 10,
        },
      ],
      heatMapChart: [
        {
          name: 'Example Tx1',
          series: [
            {
              name: 'Tx1',
              value: 10,
            },
            {
              name: 'Tx2',
              value: 10,
            },
          ],
        },
        {
          name: 'Example Tx2',
          series: [
            {
              name: 'Tx1',
              value: 10,
            },
            {
              name: 'Tx2',
              value: 10,
            },
          ],
        },
      ],
    };
    _type = _charts.hasOwnProperty(_type) ? _type : 'pieGrid';
    return _charts[_type];
  }
  private updateUnitTxStatusForNgxChart(
    unitTxStates: UnitsTransmissionStatus[]
  ): void {
    let ids: number[] = [];
    let colors: string[] = [];
    if (isNumber(this.id)) ids.push(this.id);
    if (this.type == 'advancedPieChart') ids = [0, 1, 2, 3, 4, 5, 6];
    const unitTxStatus = unitTxStates
      .filter((tx) => ids.includes(tx.transmissionStatusId))
      .map((tx) => {
        let ngxData: ngxData = {
          name: tx.transmissionStatusName,
          value: tx.transmissionMatchingUnits,
        };
        colors.push(tx.transmissionStatusColor);
        if (tx.transmissionTotalUnits != this.totalUnits) {
          this.totalUnits = tx.transmissionTotalUnits;
          if (isNumber(this.id))
            this.colorScheme = { domain: [tx.transmissionStatusColor] };
        }

        return ngxData;
      });
    if (this.type == 'advancedPieChart') {
      this.colorScheme = { domain: colors };
      // console.log(colors);
    }
    this.ngxCharts$.next(unitTxStatus);
  }
  private updateUnitTxStatusForChartJs(
    unitTxStates: UnitsTransmissionStatus[]
  ): void {
    const unitsTxStatus = unitTxStates
      .filter((tx) => [this.id].includes(tx.transmissionStatusId))
      .map((tx) => {
        const colors = [tx.transmissionStatusColor, '#808080'];
        const chartData: ChartDoughnutData = {
          id: tx.transmissionStatusId,
          datasets: [],
          labels: [],
        };
        chartData.labels = [tx.transmissionStatusName, 'Resto de unidades'];
        chartData.datasets = [
          {
            type: 'doughnut',
            backgroundColor: colors,
            hoverBackgroundColor: colors.map((_) => this.darkenHexColor(_)),
            data: [
              tx.transmissionMatchingUnits,
              tx.transmissionTotalUnits - tx.transmissionMatchingUnits,
            ],
          },
        ];
        this.pCharts$.next(chartData);
      });
  }
  darkenHexColor(hex: string, percent: number = 20): string {
    const num = parseInt(hex.slice(1), 16);
    const amt = Math.round(2.55 * percent);
    const r = (num >> 16) - amt;
    const g = ((num >> 8) & 0x00ff) - amt;
    const b = (num & 0x0000ff) - amt;
    const clamp = (val: number): number => {
      return Math.max(0, Math.min(255, val));
    };
    return `#${((1 << 24) | (clamp(r) << 16) | (clamp(g) << 8) | clamp(b))
      .toString(16)
      .slice(1)}`;
  }
}
